import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import DashboardLayout from '@/components/DashboardLayout';
import { setOrganizationBranding } from '@/components/OrganizationBrand';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import {
  User,
  Bell,
  Globe,
  Shield,
  Flame,
  Palette,
  Copy,
  ExternalLink,
  Tags,
  Plus,
  Save,
  Trash2,
  Upload,
  Mail,
  LifeBuoy,
  Server
} from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

const SettingsPage = () => {
  const { user, isAdmin, hasCapability } = useAuth();
  const canManageHeating = hasCapability('heating_admin');
  const canManageReservations = hasCapability('reservations.manage');
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ROLE_LABELS = {
  admin: 'Beheerder',
  teamleader: 'Teamleider',
  hoofd_facilitair: 'Hoofd facilitair',
  connectleider: 'Connectleider',
  leiderschap: 'Leiderschap',
  pastor: 'Voorganger',
  worshipleader: 'Worshipleader',
  coordinator_worship: 'Worshipcoördinator',
  worshipteamlid: 'Worshipteamlid',
  secretariaat: 'Secretariaat',
  employee: 'Medewerker',
  jeugdleider: 'Jeugdleider',
  tienerleider: 'Tienerleider',
  discipelschap_coordinator: 'Discipelschapcoördinator',
};
  const [heatingConfig, setHeatingConfig] = useState({
    home_assistant_base_url: '',
    home_assistant_token: '',
    heating_enabled: true,
    has_token: false,
    token_preview: null,
  });
  const [savingHeating, setSavingHeating] = useState(false);
  const [tokenTouched, setTokenTouched] = useState(false);
  const [branding, setBranding] = useState({
    name: '',
    logo_url: '',
    support_email: '',
  });
  const [savingBranding, setSavingBranding] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [relationTypes, setRelationTypes] = useState([]);
  const [newRelationType, setNewRelationType] = useState('');
  const [savingRelationType, setSavingRelationType] = useState(null);
  const [bookingSettings, setBookingSettings] = useState({ require_approval: false });
  const [savingBookingSettings, setSavingBookingSettings] = useState(false);
  const [mailLog, setMailLog] = useState([]);
  const [mailLogLoading, setMailLogLoading] = useState(true);
  const [composeForm, setComposeForm] = useState({ to: '', subject: '', body: '' });
  const [sendingMail, setSendingMail] = useState(false);
  const [supportForm, setSupportForm] = useState({ subject: '', message: '' });
  const [sendingSupport, setSendingSupport] = useState(false);
  const [smtpConfig, setSmtpConfig] = useState({
    enabled: false,
    host: '',
    port: 587,
    user: '',
    password: '',
    from_email: '',
    use_tls: true,
    has_password: false,
    password_preview: null,
  });
  const [savingSmtp, setSavingSmtp] = useState(false);
  const [smtpPasswordTouched, setSmtpPasswordTouched] = useState(false);
  const [testingSmtp, setTestingSmtp] = useState(false);
  
  const publicBookingUrl = `${window.location.origin}/book/${user?.organization_id}`;

  const copyPublicUrl = () => {
    navigator.clipboard.writeText(publicBookingUrl);
    toast.success('Link gekopieerd naar klembord');
  };

  useEffect(() => {
    const fetchBranding = async () => {
      try {
        const response = await axios.get(`${API}/organization/branding`);
        setBranding({
          name: response.data?.name || '',
          logo_url: response.data?.logo_url || '',
          support_email: response.data?.support_email || '',
        });
      } catch (error) {
        toast.error('Kon organisatiegegevens niet laden');
      }
    };
    fetchBranding();
  }, [API]);

  useEffect(() => {
    if (!isAdmin) return;
    const fetchSmtpConfig = async () => {
      try {
        const response = await axios.get(`${API}/organization/smtp`);
        setSmtpConfig((prev) => ({
          ...prev,
          enabled: !!response.data.enabled,
          host: response.data.host || '',
          port: response.data.port || 587,
          user: response.data.user || '',
          password: '',
          from_email: response.data.from_email || '',
          use_tls: response.data.use_tls !== false,
          has_password: !!response.data.has_password,
          password_preview: response.data.password_preview || null,
        }));
        setSmtpPasswordTouched(false);
      } catch (error) {
        toast.error('Kon SMTP-instellingen niet laden');
      }
    };
    fetchSmtpConfig();
  }, [API, isAdmin]);

  useEffect(() => {
    if (!isAdmin) return;
    const fetchMailLog = async () => {
      setMailLogLoading(true);
      try {
        const response = await axios.get(`${API}/organization/mail/log`);
        setMailLog(response.data);
      } catch (error) {
        toast.error('Kon verzendlog niet laden');
      } finally {
        setMailLogLoading(false);
      }
    };
    fetchMailLog();
  }, [API, isAdmin]);

  useEffect(() => {
    if (!canManageHeating) return;
    const fetchHeatingConfig = async () => {
      try {
        const response = await axios.get(`${API}/admin/heating/config`);
        setHeatingConfig((prev) => ({
          ...prev,
          home_assistant_base_url: response.data.home_assistant_base_url || '',
          heating_enabled: !!response.data.heating_enabled,
          has_token: !!response.data.has_token,
          token_preview: response.data.token_preview || null,
          home_assistant_token: '',
        }));
        setTokenTouched(false);
      } catch (error) {
        toast.error('Kon heating configuratie niet laden');
      }
    };
    fetchHeatingConfig();
  }, [API, canManageHeating]);

  useEffect(() => {
    if (!isAdmin) return;
    const fetchRelationTypes = async () => {
      try {
        const response = await axios.get(`${API}/relation-types`);
        setRelationTypes(response.data);
      } catch (error) {
        toast.error('Kon relatietypen niet laden');
      }
    };
    fetchRelationTypes();
  }, [API, isAdmin]);

  useEffect(() => {
    if (!canManageReservations) return;
    const fetchBookingSettings = async () => {
      try {
        const response = await axios.get(`${API}/organization/booking-settings`);
        setBookingSettings({ require_approval: !!response.data.require_approval });
      } catch (error) {
        toast.error('Kon boekinginstellingen niet laden');
      }
    };
    fetchBookingSettings();
  }, [API, canManageReservations]);

  const saveHeatingConfig = async (e) => {
    e.preventDefault();
    try {
      setSavingHeating(true);
      const payload = {
        home_assistant_base_url: heatingConfig.home_assistant_base_url,
        heating_enabled: heatingConfig.heating_enabled,
      };
      if (tokenTouched) {
        payload.home_assistant_token = heatingConfig.home_assistant_token;
      }
      const response = await axios.put(`${API}/admin/heating/config`, payload);
      setHeatingConfig((prev) => ({
        ...prev,
        home_assistant_token: '',
        has_token: !!response.data.has_token,
        token_preview: response.data.token_preview || null,
      }));
      setTokenTouched(false);
      toast.success('Heating configuratie opgeslagen');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Opslaan mislukt');
    } finally {
      setSavingHeating(false);
    }
  };

  const applyBranding = (data) => {
    setBranding({ name: data?.name || '', logo_url: data?.logo_url || '', support_email: data?.support_email || '' });
    setOrganizationBranding(user?.organization_id, data);
  };

  const saveBranding = async (e) => {
    e.preventDefault();
    try {
      setSavingBranding(true);
      const response = await axios.put(`${API}/organization/branding`, branding);
      applyBranding(response.data);
      toast.success('Organisatie branding opgeslagen');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Branding opslaan mislukt');
    } finally {
      setSavingBranding(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const payload = new FormData();
    payload.append('file', file);
    try {
      setUploadingLogo(true);
      const response = await axios.post(`${API}/organization/branding/logo`, payload, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      applyBranding(response.data);
      toast.success('Logo geüpload');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Logo uploaden mislukt');
    } finally {
      setUploadingLogo(false);
      e.target.value = '';
    }
  };

  const handleLogoRemove = async () => {
    try {
      setSavingBranding(true);
      const response = await axios.put(`${API}/organization/branding`, { logo_url: '' });
      applyBranding(response.data);
      toast.success('Logo verwijderd');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Verwijderen mislukt');
    } finally {
      setSavingBranding(false);
    }
  };

  const toggleBookingApproval = async (checked) => {
    try {
      setSavingBookingSettings(true);
      const response = await axios.put(`${API}/organization/booking-settings`, { require_approval: checked });
      setBookingSettings({ require_approval: !!response.data.require_approval });
      toast.success(checked ? 'Goedkeuring voor publieke boekingen ingeschakeld' : 'Goedkeuring voor publieke boekingen uitgeschakeld');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Opslaan mislukt');
    } finally {
      setSavingBookingSettings(false);
    }
  };

  const sendMail = async (e) => {
    e.preventDefault();
    try {
      setSendingMail(true);
      await axios.post(`${API}/organization/mail/send`, composeForm);
      toast.success('Mail verzonden');
      setComposeForm({ to: '', subject: '', body: '' });
      const response = await axios.get(`${API}/organization/mail/log`);
      setMailLog(response.data);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Verzenden mislukt');
    } finally {
      setSendingMail(false);
    }
  };

  const contactSupport = async (e) => {
    e.preventDefault();
    try {
      setSendingSupport(true);
      await axios.post(`${API}/organization/support/contact`, supportForm);
      toast.success('Bericht verzonden naar Zoea-support');
      setSupportForm({ subject: '', message: '' });
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Verzenden mislukt');
    } finally {
      setSendingSupport(false);
    }
  };

  const saveSmtp = async (e) => {
    e.preventDefault();
    try {
      setSavingSmtp(true);
      const payload = {
        enabled: smtpConfig.enabled,
        host: smtpConfig.host,
        port: Number(smtpConfig.port) || 587,
        user: smtpConfig.user,
        from_email: smtpConfig.from_email,
        use_tls: smtpConfig.use_tls,
      };
      if (smtpPasswordTouched) {
        payload.password = smtpConfig.password;
      }
      const response = await axios.put(`${API}/organization/smtp`, payload);
      setSmtpConfig((prev) => ({
        ...prev,
        enabled: !!response.data.enabled,
        host: response.data.host || '',
        port: response.data.port || 587,
        user: response.data.user || '',
        password: '',
        from_email: response.data.from_email || '',
        use_tls: response.data.use_tls !== false,
        has_password: !!response.data.has_password,
        password_preview: response.data.password_preview || null,
      }));
      setSmtpPasswordTouched(false);
      toast.success('SMTP-instellingen opgeslagen');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Opslaan mislukt');
    } finally {
      setSavingSmtp(false);
    }
  };

  const testSmtp = async () => {
    try {
      setTestingSmtp(true);
      const payload = {
        host: smtpConfig.host,
        port: Number(smtpConfig.port) || 587,
        user: smtpConfig.user,
        use_tls: smtpConfig.use_tls,
      };
      if (smtpPasswordTouched) {
        payload.password = smtpConfig.password;
      }
      const response = await axios.post(`${API}/organization/smtp/test`, payload);
      toast.success(response.data.message || 'Verbinding gelukt');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Verbinding testen mislukt');
    } finally {
      setTestingSmtp(false);
    }
  };

  const applySmtpPreset = (host, port) => {
    setSmtpConfig((prev) => ({ ...prev, host, port }));
  };

  const addRelationType = async () => {
    const name = newRelationType.trim();
    if (!name) return;
    try {
      const response = await axios.post(`${API}/relation-types`, { name, is_active: true });
      setRelationTypes(prev => [...prev, response.data].sort((a, b) => a.name.localeCompare(b.name)));
      setNewRelationType('');
      toast.success('Relatietype toegevoegd');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Relatietype toevoegen mislukt');
    }
  };

  const saveRelationType = async (relationType) => {
    try {
      setSavingRelationType(relationType.id);
      const response = await axios.put(`${API}/relation-types/${relationType.id}`, {
        name: relationType.name,
        is_active: relationType.is_active,
      });
      setRelationTypes(prev => prev.map(item => item.id === relationType.id ? response.data : item));
      toast.success('Relatietype opgeslagen');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Opslaan mislukt');
    } finally {
      setSavingRelationType(null);
    }
  };

  const deleteRelationType = async (relationType) => {
    if (!window.confirm(`Relatietype "${relationType.name}" verwijderen?`)) return;
    try {
      await axios.delete(`${API}/relation-types/${relationType.id}`);
      setRelationTypes(prev => prev.filter(item => item.id !== relationType.id));
      toast.success('Relatietype verwijderd');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Verwijderen mislukt');
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-3xl space-y-6" data-testid="settings-page">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-['Manrope']">Instellingen</h1>
          <p className="text-slate-500">Beheer je account en voorkeuren</p>
        </div>

        {/* Profile Settings */}
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-teal-50 rounded-xl">
                <User className="w-5 h-5 text-teal-600" />
              </div>
              <div>
                <CardTitle className="text-lg">Profiel</CardTitle>
                <CardDescription>Je persoonlijke gegevens</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Naam</Label>
                <Input value={user?.name || ''} disabled className="rounded-xl bg-slate-50" />
              </div>
              <div className="space-y-2">
                <Label>E-mail</Label>
                <Input value={user?.email || ''} disabled className="rounded-xl bg-slate-50" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Rollen</Label>
              <div className="flex flex-wrap gap-2">
                {(user?.roles || []).map((r) => (
                  <Badge key={r} variant="secondary">{ROLE_LABELS[r] || r}</Badge>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {isAdmin && (
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-50 rounded-xl">
                  <Palette className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <CardTitle className="text-lg">Organisatie branding</CardTitle>
                  <CardDescription>Gebruik de eigen naam en het eigen logo van deze kerk of organisatie</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <form onSubmit={saveBranding} className="space-y-4">
                <div className="space-y-2">
                  <Label>Organisatienaam</Label>
                  <Input
                    value={branding.name}
                    onChange={(e) => setBranding({ ...branding, name: e.target.value })}
                    placeholder="LIFE Winterswijk"
                    className="rounded-xl"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Logo</Label>
                  <div className="flex flex-wrap items-center gap-2">
                    <Button type="button" variant="outline" className="rounded-xl" disabled={uploadingLogo} onClick={() => document.getElementById('logo-upload-input')?.click()}>
                      <Upload className="w-4 h-4 mr-2" />
                      {uploadingLogo ? 'Uploaden...' : 'Logo uploaden'}
                    </Button>
                    <input id="logo-upload-input" type="file" accept="image/png,image/jpeg,image/svg+xml,image/webp" onChange={handleLogoUpload} className="hidden" />
                    {branding.logo_url && (
                      <Button type="button" variant="outline" className="rounded-xl border-red-200 text-red-600 hover:bg-red-50" onClick={handleLogoRemove}>
                        <Trash2 className="w-4 h-4 mr-2" /> Logo verwijderen
                      </Button>
                    )}
                  </div>
                  <p className="text-xs text-slate-500">
                    PNG, JPG, SVG of WEBP, max 2 MB. Geüploade logo's blijven altijd werken, in tegenstelling tot een geplakte link (bv. van Facebook), die kan verlopen of geblokkeerd worden.
                  </p>
                </div>
                <details className="text-sm">
                  <summary className="cursor-pointer text-slate-500 hover:text-slate-700">Of geef een externe afbeelding-URL op (geavanceerd)</summary>
                  <div className="mt-2 space-y-2">
                    <Input
                      value={branding.logo_url && branding.logo_url.startsWith('data:') ? '' : branding.logo_url}
                      onChange={(e) => setBranding({ ...branding, logo_url: e.target.value })}
                      placeholder="https://..."
                      className="rounded-xl"
                    />
                    <p className="text-xs text-slate-500">
                      Alleen gebruiken als je zelf een stabiele, permanente afbeelding-URL host. Klik op "Branding opslaan" om deze URL te gebruiken in plaats van een geüpload logo.
                    </p>
                  </div>
                </details>
                <div className="space-y-2">
                  <Label>Support e-mailadres</Label>
                  <Input
                    type="email"
                    value={branding.support_email}
                    onChange={(e) => setBranding({ ...branding, support_email: e.target.value })}
                    placeholder="support@jouworganisatie.nl"
                    className="rounded-xl"
                  />
                  <p className="text-xs text-slate-500">
                    Wordt gebruikt als antwoord-adres wanneer je vanuit "Mail versturen" hieronder een e-mail stuurt.
                  </p>
                </div>
                <div className="flex items-center gap-3 rounded-xl border border-slate-100 bg-slate-50 p-3">
                  <img
                    src={branding.logo_url || '/brand/zoea-origin-mark.svg'}
                    alt={branding.name || 'Zoea'}
                    className="w-12 h-12 object-contain shrink-0"
                  />
                  <div>
                    <p className="font-medium text-slate-900">{branding.name || 'Zoea'}</p>
                    <p className="text-sm text-slate-500">Voorbeeldweergave</p>
                  </div>
                </div>
                <Button type="submit" disabled={savingBranding} className="rounded-xl bg-teal-600 hover:bg-teal-700 text-white">
                  {savingBranding ? 'Opslaan...' : 'Branding opslaan'}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {isAdmin && (
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="p-2 bg-indigo-50 rounded-xl">
                  <Server className="w-5 h-5 text-indigo-600" />
                </div>
                <div>
                  <CardTitle className="text-lg">Eigen mailserver (SMTP)</CardTitle>
                  <CardDescription>Verstuur mail vanaf je eigen domein in plaats van info@zoea.nl</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <form onSubmit={saveSmtp} className="space-y-4">
                <div className="flex items-center justify-between rounded-xl border border-slate-100 bg-slate-50 p-3">
                  <div>
                    <p className="font-medium text-slate-900">Eigen mailserver gebruiken</p>
                    <p className="text-sm text-slate-500">Staat dit uit, dan verstuurt "Mail versturen" hieronder vanaf info@zoea.nl</p>
                  </div>
                  <Switch
                    checked={smtpConfig.enabled}
                    onCheckedChange={(checked) => setSmtpConfig({ ...smtpConfig, enabled: checked })}
                  />
                </div>

                <details className="text-sm rounded-xl border border-slate-100 p-3">
                  <summary className="cursor-pointer text-slate-700 font-medium">Welke gegevens vul ik in? (hulp per aanbieder)</summary>
                  <div className="mt-3 space-y-2 text-slate-600">
                    <div className="flex items-center justify-between gap-2">
                      <span>Gmail / Google Workspace — <code className="text-xs">smtp.gmail.com</code>, poort 587. Gebruik een <a href="https://support.google.com/accounts/answer/185833" target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:underline">app-wachtwoord</a>, niet je gewone wachtwoord.</span>
                      <Button type="button" variant="outline" size="sm" className="rounded-xl shrink-0" onClick={() => applySmtpPreset('smtp.gmail.com', 587)}>Invullen</Button>
                    </div>
                    <div className="flex items-center justify-between gap-2">
                      <span>Microsoft 365 / Outlook — <code className="text-xs">smtp.office365.com</code>, poort 587.</span>
                      <Button type="button" variant="outline" size="sm" className="rounded-xl shrink-0" onClick={() => applySmtpPreset('smtp.office365.com', 587)}>Invullen</Button>
                    </div>
                    <p>Eigen hosting of ander pakket: vraag de mailserver-gegevens (host, poort, gebruikersnaam, wachtwoord) op bij je hostingprovider.</p>
                  </div>
                </details>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Mailserver (host)</Label>
                    <Input
                      value={smtpConfig.host}
                      onChange={(e) => setSmtpConfig({ ...smtpConfig, host: e.target.value })}
                      placeholder="smtp.gmail.com"
                      className="rounded-xl"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Poort</Label>
                    <Input
                      type="number"
                      value={smtpConfig.port}
                      onChange={(e) => setSmtpConfig({ ...smtpConfig, port: e.target.value })}
                      placeholder="587"
                      className="rounded-xl"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Gebruikersnaam</Label>
                  <Input
                    value={smtpConfig.user}
                    onChange={(e) => setSmtpConfig({ ...smtpConfig, user: e.target.value })}
                    placeholder="info@jouworganisatie.nl"
                    className="rounded-xl"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Wachtwoord</Label>
                  <Input
                    type="password"
                    value={smtpConfig.password}
                    onChange={(e) => {
                      setSmtpPasswordTouched(true);
                      setSmtpConfig({ ...smtpConfig, password: e.target.value });
                    }}
                    placeholder={smtpConfig.has_password ? `Huidig wachtwoord: ${smtpConfig.password_preview || '••••'}` : 'Wachtwoord'}
                    className="rounded-xl"
                  />
                  <p className="text-xs text-slate-500">
                    Laat leeg om bestaand wachtwoord te behouden. Gebruik een lege string om te wissen.
                  </p>
                </div>
                <div className="space-y-2">
                  <Label>Afzenderadres</Label>
                  <Input
                    type="email"
                    value={smtpConfig.from_email}
                    onChange={(e) => setSmtpConfig({ ...smtpConfig, from_email: e.target.value })}
                    placeholder="info@jouworganisatie.nl"
                    className="rounded-xl"
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-slate-900">TLS gebruiken</p>
                    <p className="text-sm text-slate-500">Vrijwel altijd aan laten</p>
                  </div>
                  <Switch
                    checked={smtpConfig.use_tls}
                    onCheckedChange={(checked) => setSmtpConfig({ ...smtpConfig, use_tls: checked })}
                  />
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button type="submit" disabled={savingSmtp} className="rounded-xl bg-teal-600 hover:bg-teal-700 text-white">
                    {savingSmtp ? 'Opslaan...' : 'Opslaan'}
                  </Button>
                  <Button type="button" variant="outline" disabled={testingSmtp || !smtpConfig.host} className="rounded-xl" onClick={testSmtp}>
                    {testingSmtp ? 'Testen...' : 'Verbinding testen'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {isAdmin && (
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="p-2 bg-teal-50 rounded-xl">
                  <Mail className="w-5 h-5 text-teal-600" />
                </div>
                <div>
                  <CardTitle className="text-lg">Mail versturen</CardTitle>
                  <CardDescription>Verstuur een e-mail namens je organisatie, bijvoorbeeld naar een partner of relatie</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <p className="text-sm text-slate-500 -mt-2">
                Verzonden vanaf: <span className="font-medium text-slate-700">
                  {smtpConfig.enabled && smtpConfig.from_email ? smtpConfig.from_email : 'info@zoea.nl (platform-standaard)'}
                </span>
              </p>
              <form onSubmit={sendMail} className="space-y-4">
                <div className="space-y-2">
                  <Label>Aan</Label>
                  <Input
                    type="email" required
                    value={composeForm.to}
                    onChange={(e) => setComposeForm({ ...composeForm, to: e.target.value })}
                    className="rounded-xl"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Onderwerp</Label>
                  <Input
                    required
                    value={composeForm.subject}
                    onChange={(e) => setComposeForm({ ...composeForm, subject: e.target.value })}
                    className="rounded-xl"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Bericht</Label>
                  <Textarea
                    required rows={5}
                    value={composeForm.body}
                    onChange={(e) => setComposeForm({ ...composeForm, body: e.target.value })}
                  />
                </div>
                <Button type="submit" disabled={sendingMail} className="rounded-xl bg-teal-600 hover:bg-teal-700 text-white">
                  {sendingMail ? 'Verzenden...' : 'Verzenden'}
                </Button>
              </form>

              {mailLog.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-slate-900 mb-2">Verzonden mails</p>
                  {mailLogLoading ? (
                    <p className="text-slate-500 text-sm">Laden...</p>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Aan</TableHead>
                          <TableHead>Onderwerp</TableHead>
                          <TableHead>Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {mailLog.map((entry) => (
                          <TableRow key={entry.id}>
                            <TableCell>{entry.to}</TableCell>
                            <TableCell>{entry.subject}</TableCell>
                            <TableCell>
                              <Badge className={entry.status === 'sent' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}>
                                {entry.status === 'sent' ? 'Verzonden' : 'Mislukt'}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {isAdmin && (
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-50 rounded-xl">
                  <LifeBuoy className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <CardTitle className="text-lg">Contact met Zoea-support</CardTitle>
                  <CardDescription>Stuur een vraag of melding rechtstreeks naar het Zoea-team</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <form onSubmit={contactSupport} className="space-y-4">
                <div className="space-y-2">
                  <Label>Onderwerp</Label>
                  <Input
                    required
                    value={supportForm.subject}
                    onChange={(e) => setSupportForm({ ...supportForm, subject: e.target.value })}
                    className="rounded-xl"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Bericht</Label>
                  <Textarea
                    required rows={4}
                    value={supportForm.message}
                    onChange={(e) => setSupportForm({ ...supportForm, message: e.target.value })}
                  />
                </div>
                <Button type="submit" disabled={sendingSupport} className="rounded-xl bg-teal-600 hover:bg-teal-700 text-white">
                  {sendingSupport ? 'Verzenden...' : 'Verstuur naar support'}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {isAdmin && (
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-50 rounded-xl">
                  <Tags className="w-5 h-5 text-emerald-700" />
                </div>
                <div>
                  <CardTitle className="text-lg">Relatietypen</CardTitle>
                  <CardDescription>Beheer de typen voor externe personen en organisaties</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-2">
                <Input
                  value={newRelationType}
                  onChange={(event) => setNewRelationType(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      addRelationType();
                    }
                  }}
                  placeholder="Nieuw relatietype"
                  className="rounded-xl"
                />
                <Button type="button" onClick={addRelationType} disabled={!newRelationType.trim()} className="rounded-xl bg-teal-600 hover:bg-teal-700 text-white shrink-0">
                  <Plus className="w-4 h-4 mr-2" />
                  Toevoegen
                </Button>
              </div>

              <div className="divide-y divide-slate-100 rounded-xl border border-slate-200">
                {relationTypes.map(relationType => (
                  <div key={relationType.id} className="flex flex-col sm:flex-row sm:items-center gap-3 p-3">
                    <Input
                      value={relationType.name}
                      onChange={(event) => setRelationTypes(prev => prev.map(item => item.id === relationType.id ? { ...item, name: event.target.value } : item))}
                      className="rounded-xl flex-1"
                    />
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-slate-500">Actief</span>
                      <Switch
                        checked={relationType.is_active}
                        onCheckedChange={(checked) => setRelationTypes(prev => prev.map(item => item.id === relationType.id ? { ...item, is_active: checked } : item))}
                      />
                      <Button type="button" variant="outline" size="sm" onClick={() => saveRelationType(relationType)} disabled={savingRelationType === relationType.id} className="rounded-xl">
                        <Save className="w-4 h-4" />
                      </Button>
                      {!relationType.system_key && (
                        <Button type="button" variant="outline" size="sm" onClick={() => deleteRelationType(relationType)} className="rounded-xl border-red-200 text-red-600 hover:bg-red-50">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-xs text-slate-500">Standaardtypen kunnen worden hernoemd of gedeactiveerd. Zelf toegevoegde typen kunnen worden verwijderd zolang ze niet gebruikt worden.</p>
            </CardContent>
          </Card>
        )}

        {canManageHeating && (
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="p-2 bg-orange-50 rounded-xl">
                  <Flame className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <CardTitle className="text-lg">Heating integratie</CardTitle>
                  <CardDescription>Stel Home Assistant URL en token in voor automatische verwarming</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <form onSubmit={saveHeatingConfig} className="space-y-4">
                <div className="space-y-2">
                  <Label>Home Assistant Base URL</Label>
                  <Input
                    value={heatingConfig.home_assistant_base_url}
                    onChange={(e) => setHeatingConfig({ ...heatingConfig, home_assistant_base_url: e.target.value })}
                    placeholder="http://homeassistant.local:8123"
                    className="rounded-xl"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Long-Lived Access Token</Label>
                  <Input
                    type="password"
                    value={heatingConfig.home_assistant_token}
                    onChange={(e) => {
                      setTokenTouched(true);
                      setHeatingConfig({ ...heatingConfig, home_assistant_token: e.target.value });
                    }}
                    placeholder={heatingConfig.has_token ? `Huidig token: ${heatingConfig.token_preview || '••••'}` : 'Plak token hier'}
                    className="rounded-xl"
                  />
                  <p className="text-xs text-slate-500">
                    Laat leeg om bestaand token te behouden. Gebruik een lege string om te wissen.
                  </p>
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-slate-900">Heating actief</p>
                    <p className="text-sm text-slate-500">Kill switch voor uitvoeren van heating acties</p>
                  </div>
                  <Switch
                    checked={heatingConfig.heating_enabled}
                    onCheckedChange={(checked) => setHeatingConfig({ ...heatingConfig, heating_enabled: checked })}
                  />
                </div>
                <Button type="submit" disabled={savingHeating} className="rounded-xl bg-teal-600 hover:bg-teal-700 text-white">
                  {savingHeating ? 'Opslaan...' : 'Heating instellingen opslaan'}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Public Booking Page */}
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-50 rounded-xl">
                <Globe className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <CardTitle className="text-lg">Publieke boekingspagina</CardTitle>
                <CardDescription>Deel deze link met klanten voor online reserveren</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-2">
              <Input 
                value={publicBookingUrl} 
                readOnly 
                className="rounded-xl bg-slate-50 font-mono text-sm"
              />
              <Button 
                variant="outline" 
                onClick={copyPublicUrl}
                className="rounded-xl shrink-0"
              >
                <Copy className="w-4 h-4 mr-2" />
                Kopiëren
              </Button>
            </div>
            <a
              href={publicBookingUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-sm text-teal-600 hover:text-teal-700"
            >
              Bekijk pagina
              <ExternalLink className="w-4 h-4" />
            </a>
            {canManageReservations && (
              <>
                <Separator />
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <p className="text-sm font-medium text-slate-900">Goedkeuring vereist voor publieke aanvragen</p>
                    <p className="text-sm text-slate-500">
                      Staat dit aan, dan wordt een publieke boekingsaanvraag pas bevestigd nadat een facilitair manager of leiderschap deze goedkeurt. De aanvrager krijgt daarna automatisch een e-mail.
                    </p>
                  </div>
                  <Switch
                    checked={bookingSettings.require_approval}
                    onCheckedChange={toggleBookingApproval}
                    disabled={savingBookingSettings}
                  />
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Notification Settings */}
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-50 rounded-xl">
                <Bell className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <CardTitle className="text-lg">Notificaties</CardTitle>
                <CardDescription>Beheer je notificatievoorkeuren</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-slate-900">E-mail notificaties</p>
                <p className="text-sm text-slate-500">Ontvang e-mails bij nieuwe boekingen</p>
              </div>
              <Switch defaultChecked />
            </div>
            <Separator />
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-slate-900">Herinneringen</p>
                <p className="text-sm text-slate-500">Stuur herinneringen naar klanten</p>
              </div>
              <Switch defaultChecked />
            </div>
            <Separator />
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-slate-900">Dagelijkse samenvatting</p>
                <p className="text-sm text-slate-500">Ontvang elke ochtend een overzicht</p>
              </div>
              <Switch />
            </div>
          </CardContent>
        </Card>

        {/* Appearance */}
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-50 rounded-xl">
                <Palette className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <CardTitle className="text-lg">Weergave</CardTitle>
                <CardDescription>Pas de look van je dashboard aan</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-slate-900">Donkere modus</p>
                <p className="text-sm text-slate-500">Schakel naar donker thema</p>
              </div>
              <Switch />
            </div>
            <Separator />
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-slate-900">Compacte weergave</p>
                <p className="text-sm text-slate-500">Toon meer informatie per pagina</p>
              </div>
              <Switch />
            </div>
          </CardContent>
        </Card>

        {/* Security */}
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-50 rounded-xl">
                <Shield className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <CardTitle className="text-lg">Beveiliging</CardTitle>
                <CardDescription>Beveilig je account</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <Button variant="outline" className="rounded-xl">
              Wachtwoord wijzigen
            </Button>
            <Separator />
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-slate-900">Twee-factor authenticatie</p>
                <p className="text-sm text-slate-500">Extra beveiligingslaag voor je account</p>
              </div>
              <Button variant="outline" size="sm" className="rounded-xl">
                Instellen
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* GDPR */}
        <Card className="border-0 shadow-sm border-l-4 border-l-teal-500">
          <CardContent className="py-4">
            <div className="flex items-start gap-4">
              <Shield className="w-6 h-6 text-teal-600 mt-1" />
              <div>
                <h3 className="font-medium text-slate-900">GDPR Compliant</h3>
                <p className="text-sm text-slate-500 mt-1">
                  Zoea voldoet aan de Europese privacywetgeving. Je gegevens worden veilig 
                  opgeslagen en je hebt altijd de controle over je data.
                </p>
                <div className="flex gap-4 mt-3">
                  <a href="#" className="text-sm text-teal-600 hover:underline">Privacy Policy</a>
                  <a href="#" className="text-sm text-teal-600 hover:underline">Gegevens exporteren</a>
                  <a href="#" className="text-sm text-red-600 hover:underline">Account verwijderen</a>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default SettingsPage;
